import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, User } from "@phosphor-icons/react";

const ROLE_META = {
  driver: {
    label: "DRIVER",
    badge: "bg-black text-white",
  },
  loader: {
    label: "LOADER",
    badge: "border border-black bg-white text-black",
  },
  agency_driver: {
    label: "AGENCY DRIVER",
    badge: "bg-purple-700 text-white",
  },
  agency_loader: {
    label: "AGENCY LOADER",
    badge: "bg-purple-500 text-white",
  },
};
const ROLE_ORDER = ["driver", "loader", "agency_driver", "agency_loader"];
const FILTER_LABELS = { ALL: "ALL", DRIVER: "driver", LOADER: "loader", "AGENCY DRIVER": "agency_driver", "AGENCY LOADER": "agency_loader" };

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role: "loader" });

  const load = async () => {
    try {
      const s = await api.listStaff();
      setStaff(s);
    } catch (e) {
      toast.error("Failed to load staff");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = staff;
    if (roleFilter !== "ALL") {
      const target = FILTER_LABELS[roleFilter];
      list = list.filter((s) => s.role === target);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q));
    }
    return list;
  }, [staff, search, roleFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", role: "loader" });
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, role: s.role });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name required");
      return;
    }
    try {
      if (editing) await api.updateStaff(editing.id, form);
      else await api.createStaff(form);
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Updated" : "Added");
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This removes all their assignments.`)) return;
    try {
      await api.deleteStaff(s.id);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const drivers = staff.filter((s) => s.role === "driver").length;
  const loaders = staff.filter((s) => s.role === "loader").length;
  const agencyDrivers = staff.filter((s) => s.role === "agency_driver").length;
  const agencyLoaders = staff.filter((s) => s.role === "agency_loader").length;

  return (
    <div className="p-6 min-h-screen">
      <header className="flex items-end justify-between border-b-2 border-black pb-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-none">STAFF DIRECTORY</h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-2">
            {staff.length} TOTAL · {drivers} DRIVERS · {loaders} LOADERS · {agencyDrivers} AGENCY DRIVERS · {agencyLoaders} AGENCY LOADERS
          </div>
        </div>
        <Button
          onClick={openNew}
          data-testid="add-staff-btn"
          className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
        >
          <Plus size={14} weight="bold" className="mr-2" /> ADD STAFF
        </Button>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          data-testid="staff-search-input"
          className="rounded-none border-black w-64 font-mono-plex text-xs"
        />
        <div className="flex border border-black">
          {["ALL", "DRIVER", "LOADER", "AGENCY DRIVER", "AGENCY LOADER"].map((r) => {
            const staffRole = FILTER_LABELS[r];
            const purple = r === "AGENCY DRIVER" || r === "AGENCY LOADER";
            const active = roleFilter === r;
            return (
              <button
                key={r}
                onClick={() => setRoleFilter(r)}
                data-testid={`role-filter-${r.replace(/\s+/g, "-")}`}
                className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors border-r border-black last:border-r-0 ${
                  active
                    ? purple
                      ? "bg-purple-700 text-white"
                      : "bg-black text-white"
                    : "bg-white hover:bg-zinc-100"
                }`}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-2 border-black bg-white">
        <div className="grid grid-cols-[1fr_140px_120px] font-heading font-bold text-[10px] tracking-widest border-b-2 border-black bg-zinc-50">
          <div className="px-4 py-3">NAME</div>
          <div className="px-4 py-3 border-l border-zinc-300">ROLE</div>
          <div className="px-4 py-3 border-l border-zinc-300 text-right">ACTIONS</div>
        </div>
        {filtered.map((s) => (
          <div
            key={s.id}
            className="grid grid-cols-[1fr_140px_120px] border-b border-zinc-200 hover:bg-zinc-50"
            data-testid={`staff-row-${s.id}`}
          >
            <div className="px-4 py-3 flex items-center gap-2 font-mono-plex text-sm">
              <User size={14} className="text-zinc-500" />
              {s.name}
            </div>
            <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-[10px] tracking-widest">
              <span
                className={`inline-block px-2 py-0.5 ${
                  (ROLE_META[s.role] || ROLE_META.loader).badge
                }`}
              >
                {(ROLE_META[s.role] || ROLE_META.loader).label}
              </span>
            </div>
            <div className="px-4 py-3 border-l border-zinc-200 flex items-center justify-end gap-2">
              <button
                onClick={() => openEdit(s)}
                data-testid={`edit-staff-${s.id}`}
                className="p-1.5 border border-black hover:bg-black hover:text-white transition-colors"
              >
                <PencilSimple size={12} weight="bold" />
              </button>
              <button
                onClick={() => del(s)}
                data-testid={`delete-staff-${s.id}`}
                className="p-1.5 border border-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
              >
                <Trash size={12} weight="bold" />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-6 text-center font-mono-plex text-xs text-zinc-500">
            NO STAFF FOUND
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editing ? "EDIT STAFF" : "ADD STAFF"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">NAME</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="staff-form-name"
                className="rounded-none border-black font-mono-plex"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">ROLE</label>
              <div className="grid grid-cols-2 gap-0 border border-black">
                {ROLE_ORDER.map((r) => {
                  const meta = ROLE_META[r];
                  const active = form.role === r;
                  const purple = r.startsWith("agency_");
                  return (
                    <button
                      key={r}
                      onClick={() => setForm({ ...form, role: r })}
                      data-testid={`staff-form-role-${r}`}
                      className={`py-2 text-[10px] font-mono-plex tracking-widest transition-colors border-b border-r border-black last:border-b-0 [&:nth-last-child(2)]:border-b-0 [&:nth-child(2n)]:border-r-0 ${
                        active
                          ? purple
                            ? "bg-purple-700 text-white"
                            : "bg-black text-white"
                          : "bg-white hover:bg-zinc-100"
                      }`}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              data-testid="staff-form-save"
              className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
            >
              SAVE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
